import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import TimelineFirestoreService from '../services/TimelineFirestoreService';
import FilterPanel from './FilterPanel';
import ZoomController from './ZoomController';
import TimelineRenderer from './TimelineRenderer';
import EventDetailPanel from './EventDetailPanel';
import EventEditor from './EventEditor';
import { getMaxVisibleDepth } from '../utils/dateUtils';
import '../styles/TimelinePage.css';

const service = new TimelineFirestoreService();

const LIVE_COLLECTION = 'timeline_events';
const TEST_COLLECTION = 'timeline_events_test';

/**
 * TimelinePage - Main container component for the Historical Timeline.
 * Manages state for visible range, selected/editing events, filters, and event data.
 * Coordinates CRUD operations through TimelineFirestoreService.
 */
function TimelinePage() {
  const [visibleRange, setVisibleRange] = useState({ start: -4000, end: 2100 });
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [editingEvent, setEditingEvent] = useState(null);
  const [showEditor, setShowEditor] = useState(false);
  const [filters, setFilters] = useState({ category: null, tags: [] });
  const [events, setEvents] = useState([]);
  const [incomingLinks, setIncomingLinks] = useState([]);
  const [useTestData, setUseTestData] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();

  const rangeSize = visibleRange.end - visibleRange.start;
  const maxDepth = getMaxVisibleDepth(rangeSize);

  // Switch collection when toggling between live/test data
  useEffect(() => {
    service.setCollection(useTestData ? TEST_COLLECTION : LIVE_COLLECTION);
    setSelectedEvent(null);
  }, [useTestData]);

  /**
   * Toggle between live and test data.
   */
  const handleToggleTestData = () => {
    setUseTestData((prev) => !prev);
  };

  /**
   * Load events from Firestore for the current visible range and depth.
   * Applies client-side filtering by category/tag after range query.
   */
  const loadEvents = useCallback(async () => {
    try {
      let result = await service.queryByRange(visibleRange.start, visibleRange.end);

      // Apply client-side category filter
      if (filters.category) {
        result = result.filter((event) => event.category === filters.category);
      }

      // Apply client-side tag filter
      if (filters.tags.length > 0) {
        result = result.filter((event) =>
          event.tags && filters.tags.every((tag) => event.tags.includes(tag))
        );
      }

      setEvents(result);
    } catch (error) {
      console.error('Failed to load timeline events:', error);
    }
  }, [visibleRange.start, visibleRange.end, filters.category, filters.tags, useTestData]);

  // Load events on mount and when range/filters change
  useEffect(() => {
    loadEvents();
  }, [loadEvents]);

  // Handle ?event=ID query param — auto-select and zoom to event from map link
  useEffect(() => {
    const eventId = searchParams.get('event');
    if (eventId) {
      service.getEvent(eventId).then(event => {
        if (event) {
          setSelectedEvent(event);
          // Zoom to a 200-year window around the event
          const year = event.startYear;
          setVisibleRange({ start: year - 100, end: year + 100 });
        }
      }).catch(console.error);
      // Clear the param so it doesn't re-trigger
      setSearchParams({}, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  // Load incoming links when an event is selected
  useEffect(() => {
    if (selectedEvent) {
      service.queryIncomingLinks(selectedEvent.id)
        .then(setIncomingLinks)
        .catch((error) => {
          console.error('Failed to load incoming links:', error);
          setIncomingLinks([]);
        });
    } else {
      setIncomingLinks([]);
    }
  }, [selectedEvent]);

  /**
   * Handle range change from zoom/pan interactions.
   */
  const handleRangeChange = (start, end) => {
    setVisibleRange({ start, end });
  };

  /**
   * Handle filter changes from FilterPanel.
   */
  const handleFilterChange = (newFilters) => {
    setFilters(newFilters);
  };

  /**
   * Handle event click - select event and show detail panel.
   */
  const handleEventClick = (event) => {
    setSelectedEvent(event);
  };

  /**
   * Close the detail panel.
   */
  const handleCloseDetail = () => {
    setSelectedEvent(null);
  };

  /**
   * Open editor in create mode (new event).
   */
  const handleAddEvent = () => {
    setEditingEvent(null);
    setShowEditor(true);
  };

  /**
   * Open editor in edit mode for the selected event.
   */
  const handleEditEvent = () => {
    setEditingEvent(selectedEvent);
    setShowEditor(true);
  };

  /**
   * Save event (create or update) and reload events.
   */
  const handleSaveEvent = async (eventData) => {
    try {
      if (editingEvent) {
        await service.updateEvent(editingEvent.id, eventData);
      } else {
        await service.createEvent(eventData);
      }
      setShowEditor(false);
      setEditingEvent(null);
      await loadEvents();
    } catch (error) {
      console.error('Failed to save event:', error);
    }
  };

  /**
   * Cancel editing - close the editor modal.
   */
  const handleCancelEditor = () => {
    setShowEditor(false);
    setEditingEvent(null);
  };

  /**
   * Delete the selected event and reload events.
   */
  const handleDeleteEvent = async () => {
    if (!selectedEvent) return;
    try {
      await service.deleteEvent(selectedEvent.id);
      setSelectedEvent(null);
      await loadEvents();
    } catch (error) {
      console.error('Failed to delete event:', error);
    }
  };

  /**
   * Navigate to a linked event by selecting it.
   */
  const handleNavigateToEvent = (eventId) => {
    const targetEvent = events.find((e) => e.id === eventId);
    if (targetEvent) {
      setSelectedEvent(targetEvent);
    }
  };

  return (
    <div className="timeline-page">
      <div className="timeline-page-header">
        <h1 className="timeline-page-title">Historical Timeline</h1>
        <div className="timeline-header-actions">
          <button
            className={`timeline-toggle-data-btn ${useTestData ? 'active' : ''}`}
            onClick={handleToggleTestData}
            aria-pressed={useTestData}
          >
            {useTestData ? '📋 Test Data' : '📋 Live Data'}
          </button>
          <button
            className="timeline-add-event-btn"
            onClick={handleAddEvent}
            aria-label="Add Event"
          >
            Add Event
          </button>
        </div>
      </div>

      <FilterPanel
        onFilterChange={handleFilterChange}
        availableTags={[]}
        activeFilters={filters}
      />

      <ZoomController
        visibleRange={visibleRange}
        onRangeChange={handleRangeChange}
      />

      <TimelineRenderer
        events={events}
        visibleRange={visibleRange}
        maxDepth={maxDepth}
        onEventClick={handleEventClick}
        onRangeChange={handleRangeChange}
      />

      {selectedEvent && (
        <EventDetailPanel
          event={selectedEvent}
          incomingLinks={incomingLinks}
          onEdit={handleEditEvent}
          onDelete={handleDeleteEvent}
          onNavigateToEvent={handleNavigateToEvent}
          onClose={handleCloseDetail}
        />
      )}

      {showEditor && (
        <EventEditor
          event={editingEvent}
          onSave={handleSaveEvent}
          onCancel={handleCancelEditor}
          existingEvents={events}
        />
      )}
    </div>
  );
}

export default TimelinePage;
